import {
  quoteLineType,
  repCodeType,
  salesforceAccountType,
  salesforceContactType,
  salesforceOpportunityType,
} from 'src/types'
import { formatCurrency } from 'src/helper'
import dayjs from 'dayjs'

export type AddressDetails = {
  accountCompany: string
  contactAttn: string
  addressLine1: string
  city: string
  state: string
  zipCode: string
  phone: string
  email: string
}

export type ContactDetails = {
  name: string
  title: string
  phone: string
  email: string
}

export type PreviewRowKind = 'bundle' | 'bundleItem' | 'item' | 'groupTotal'

export type PreviewRow = {
  kind: PreviewRowKind
  cells: (string | number)[]
}

export const EMPTY_ADDRESS: AddressDetails = {
  accountCompany: '',
  contactAttn: '',
  addressLine1: '',
  city: '',
  state: '',
  zipCode: '',
  phone: '',
  email: '',
}

export const EMPTY_CONTACT: ContactDetails = {
  name: '',
  title: '',
  phone: '',
  email: '',
}

const ADDRESS_FIELDS = ['Account/Company', 'Contact (Attn:)', 'Address', 'City, State, ZIP Code', 'Phone', 'Email']

export const LINE_HEADERS = [
  'Line',
  'QTY',
  'Contract',
  'Manufacturer',
  'MFR Part #',
  'Description',
  'TAA',
  'Unit Price',
  'Ext Price',
]

type CrmAddress = {
  street: string | null
  city: string | null
  state: string | null
  postalCode: string | null
} | null

function itemCells(line: quoteLineType): (string | number)[] {
  return [
    line.LineNumber,
    line.Qty ?? '-',
    line.ContractCatalog ?? '-',
    line.Manufacturer ?? '-',
    line.MfrPartNumber ?? '-',
    [
      line.Description,
      line.StartDate ? '\nStart Date: ' + dayjs(line.StartDate).format('MM/DD/YYYY') : '',
      line.EndDate ? '\nEnd Date: ' + dayjs(line.EndDate).format('MM/DD/YYYY') : '',
    ].join(''),
    line.TAA == null ? '-' : line.TAA ? 'Yes' : 'No',
    formatCurrency(line.UnitPrice ?? 0),
    formatCurrency(line.ExtPrice ?? 0),
  ]
}

export function buildLineTables(lineItems: (quoteLineType & { children?: quoteLineType[] })[]): PreviewRow[][] {
  const tables: PreviewRow[][] = []
  for (const lineItem of lineItems) {
    if (lineItem.children && lineItem.LineNumber === -1) {
      tables.push([
        { kind: 'bundle', cells: [[lineItem.MfrPartNumber, lineItem.Description].filter(Boolean).join(' : ')] },
        ...lineItem.children.map(child => ({ kind: 'bundleItem' as const, cells: itemCells(child) })),
        {
          kind: 'groupTotal',
          cells: ['', lineItem.Qty ?? '', '', '', '', '', '', 'Group Total', formatCurrency(lineItem.ExtPrice ?? 0)],
        },
      ])
    } else {
      const last = tables.at(-1)
      const row: PreviewRow = { kind: 'item', cells: itemCells(lineItem) }
      if (last?.[0].kind === 'item') last.push(row)
      else tables.push([row])
    }
  }
  return tables
}

export function addressLines(details: AddressDetails): string[] {
  const filled =
    details.accountCompany ||
    details.contactAttn ||
    details.addressLine1 ||
    details.city ||
    details.state ||
    details.zipCode ||
    details.phone ||
    details.email
  if (!filled) return [...ADDRESS_FIELDS]

  const cityStateZip = [details.city, [details.state, details.zipCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')

  return [
    details.accountCompany,
    details.contactAttn,
    details.addressLine1,
    cityStateZip,
    details.phone,
    details.email,
  ].filter(Boolean)
}

export function signatureBlock(isr: ContactDetails, ae: ContactDetails): { left: string[]; right: string[] } {
  return {
    left: [isr.name, isr.title || 'Inside Sales Rep', isr.phone, isr.email],
    right: [ae.name, ae.title || 'Account Executive', ae.phone, ae.email],
  }
}

function addressFromCrmParts(parts: {
  accountCompany: string
  contactAttn: string
  address: CrmAddress
  phone: string
  email: string
}): AddressDetails {
  return {
    accountCompany: parts.accountCompany,
    contactAttn: parts.contactAttn,
    addressLine1: parts.address?.street ?? '',
    city: parts.address?.city ?? '',
    state: parts.address?.state ?? '',
    zipCode: parts.address?.postalCode ?? '',
    phone: parts.phone,
    email: parts.email,
  }
}

export function soldToFromCrm(
  account: salesforceAccountType | null,
  contact: salesforceContactType | null,
): AddressDetails {
  if (!account && !contact) return { ...EMPTY_ADDRESS }
  return addressFromCrmParts({
    accountCompany: account?.Name ?? '',
    contactAttn: contact?.Name ?? '',
    address: contact?.Default_Billing_Address__c ?? null,
    phone: contact?.Phone ?? '',
    email: contact?.Email ?? '',
  })
}

export function shipToFromCrm(
  account: salesforceAccountType | null,
  contact: salesforceContactType | null,
): AddressDetails {
  if (!account && !contact) return { ...EMPTY_ADDRESS }
  return addressFromCrmParts({
    accountCompany: account?.Name ?? '',
    contactAttn: contact?.Name ?? '',
    address: contact?.Default_Shipping_Address__c ?? null,
    phone: contact?.Phone ?? '',
    email: contact?.Email ?? '',
  })
}

export function isrFromCrm(crm: salesforceOpportunityType | null): ContactDetails {
  if (!crm) return { ...EMPTY_CONTACT }
  return {
    name: crm.Owner.Name,
    title: 'Inside Sales Rep',
    phone: crm.Owner.Phone ?? '',
    email: crm.Owner.Email ?? '',
  }
}

export function aeFromCrm(crm: salesforceOpportunityType | null): ContactDetails {
  if (!crm?.Account) return { ...EMPTY_CONTACT }
  return {
    name: crm.Account.Owner.Name,
    title: 'Account Executive',
    phone: crm.Account.Owner.Phone ?? '',
    email: crm.Account.Owner.Email ?? '',
  }
}

export function isrFromRepCode(repCode: repCodeType | null): ContactDetails {
  if (!repCode) return { ...EMPTY_CONTACT }
  return {
    name: repCode.InsideSalesRep,
    title: 'Inside Sales Rep',
    phone: '',
    email: repCode.IsrEmail ?? '',
  }
}

export function aeFromRepCode(repCode: repCodeType | null): ContactDetails {
  if (!repCode) return { ...EMPTY_CONTACT }
  return {
    name: repCode.AccountExecutive ?? '',
    title: 'Account Executive',
    phone: '',
    email: repCode.AeEmail ?? '',
  }
}
